'use client';

import * as React from 'react';
import Link from 'next/link';
import { TrendingUpIcon } from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';

export function RevenueHubHero(): React.JSX.Element {
  return (
    <GridSection>
      <div className="container space-y-12 py-20 md:py-32">
        {/* Badge */}
        <motion.div
          initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
          animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center justify-center"
        >
          <Badge
            variant="outline"
            className="h-8 rounded-full px-3 text-sm font-medium shadow-xs"
          >
            <TrendingUpIcon className="mr-2 size-4 shrink-0" />
            Revenue Hub
          </Badge>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <SiteHeading
            title="Direct inzicht in je verwachte omzet en betalingen"
            description="Met Emma's Revenue Hub krijg je een compleet overzicht van je financiële prestaties. Volg je omzet, analyseer trends, voorspel toekomstige inkomsten en houd je doelen in de gaten. Alles wat je nodig hebt om je bedrijf te laten groeien."
          />
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="mx-auto flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:justify-center"
        >
          <Link
            href={routes.dashboard.auth.SignUp}
            className={cn(
              buttonVariants({
                variant: 'default'
              }),
              'h-10 rounded-xl sm:h-9'
            )}
          >
            Schrijf je nu in en start direct!
          </Link>
          <Link
            href={routes.marketing.Contact}
            className={cn(
              buttonVariants({
                variant: 'outline'
              }),
              'h-10 rounded-xl sm:h-9'
            )}
          >
            Neem contact op
          </Link>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="mx-auto mt-12 max-w-5xl"
        >
          <div className="relative aspect-video w-full overflow-hidden rounded-xl border bg-muted shadow-lg">
            <img
              src="/assets/hero/revenuehub.png"
              alt="Revenue Hub dashboard"
              className="h-full w-full object-cover"
            />
          </div>
        </motion.div>
      </div>
    </GridSection>
  );
}
